/**
 * Single source of truth for PDF embeds.
 *
 * A PDF embed is persisted inside card/note content as the markdown marker
 * `[PDF](https://example.com/doc.pdf){width=800 height=600}` and is rendered as
 *
 *   <div class="pdf-embed-wrapper" data-pdf-embed data-src=… data-width data-height>
 *     <iframe class="pdf-embed-frame" src=… width=… height=… loading="lazy"></iframe>
 *     <a class="pdf-embed-link" href=… target="_blank">doc.pdf</a>
 *   </div>
 *
 * Why an <iframe> and not an <object>: our global CSP (public/_headers) sets
 * `object-src 'none'` to block plugin-based XSS, which makes every
 * `<object type="application/pdf">` fail to load and fall through to its
 * fallback link ("View PDF"). Iframes are unrestricted by that policy and every
 * browser renders PDFs in one.
 *
 * Why the link is a *sibling* of the iframe and not iframe fallback content:
 * browsers ignore an iframe's children, and the strict sanitizer
 * (`sanitizeHtml`) drops the iframe on untrusted surfaces (card previews, link
 * notes, shared cards). Keeping the link outside means those surfaces degrade to
 * a labelled link instead of an empty box. The fixed height therefore lives on
 * the iframe, never on the wrapper, so the wrapper collapses when it is stripped.
 *
 * The label is the PDF's filename, which is both more useful than a generic
 * "View PDF" and needs no translation.
 *
 * The frame is deliberately NOT sandboxed: Chrome refuses to run its built-in
 * PDF viewer inside a sandboxed frame — verified locally, *any* sandbox value
 * (including `allow-scripts allow-same-origin`) renders a blank frame while the
 * unsandboxed one shows the document. The exposure is bounded instead by the
 * sanitizers: the strict `sanitizeHtml` (card previews, notes, shared cards)
 * drops the iframe entirely and leaves the link.
 */

import { safeDecodeURIComponent } from './safeDecodeURIComponent.js'
import { escapeHtmlText, isSafeHref } from './security.js'

export const PDF_EMBED_DEFAULT_WIDTH = 800
export const PDF_EMBED_DEFAULT_HEIGHT = 600

/** Matches the persisted markdown marker. Global — callers use it with replace/matchAll. */
export const PDF_MARKDOWN_PATTERN = /\[PDF\]\(([^)]+)\)\{width=(\d+)\s+height=(\d+)\}/gi

/** A marker cut in half by a preview truncation, e.g. `…[PDF](https://e.com/a.p`. */
const PARTIAL_PDF_MARKDOWN_PATTERN = /\[PDF\](?:\([^)]*(?:\)\{[^}]*)?)?$/


/**
 * Whether a PDF source URL is safe to render — the app-wide link policy from
 * utils/security.js, minus `mailto:` (there is nothing to embed there).
 */
export function isSafePdfSrc(src) {
  return isSafeHref(src)
}

/**
 * Human label for an embed: the PDF's filename, falling back to "PDF" (an
 * acronym that reads the same in every locale, so no translation is needed).
 */
export function pdfEmbedLabel(src) {
  if (!src || typeof src !== 'string') return 'PDF'
  const withoutQuery = src.split(/[?#]/)[0]
  const filename = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1)
  const decoded = safeDecodeURIComponent(filename).trim()
  return decoded || 'PDF'
}

function toDimension(value, fallback) {
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * Render one PDF embed to HTML. Returns '' for unsafe sources so a malicious
 * marker renders nothing at all rather than a live element.
 */
export function renderPdfEmbedHtml(src, width, height) {
  if (!isSafePdfSrc(src)) return ''
  const w = toDimension(width, PDF_EMBED_DEFAULT_WIDTH)
  const h = toDimension(height, PDF_EMBED_DEFAULT_HEIGHT)
  const safeSrc = escapeHtmlText(src.trim())
  const label = escapeHtmlText(pdfEmbedLabel(src))

  return (
    `<div class="pdf-embed-wrapper" data-pdf-embed="" data-src="${safeSrc}" data-width="${w}" data-height="${h}">` +
    `<iframe class="pdf-embed-frame" src="${safeSrc}" width="${w}" height="${h}" ` +
    `style="height: ${h}px;" loading="lazy" referrerpolicy="no-referrer" ` +
    `title="${label}"></iframe>` +
    `<a class="pdf-embed-link" href="${safeSrc}" target="_blank" rel="noopener noreferrer">${label}</a>` +
    `</div>`
  )
}

/**
 * Replace every PDF marker in `markdown` with its HTML, via `onEmbed` so callers
 * that post-process the text (escaping, markdown-it) can stash the HTML behind a
 * placeholder first. Without `onEmbed` the HTML is inlined directly.
 */
export function replacePdfMarkdown(markdown, onEmbed) {
  if (!markdown || typeof markdown !== 'string') return markdown || ''
  if (!markdown.includes('[PDF]')) return markdown
  return markdown.replace(PDF_MARKDOWN_PATTERN, (match, src, width, height) => {
    const html = renderPdfEmbedHtml(src, width, height)
    if (!html) return ''
    return onEmbed ? onEmbed(html) : html
  })
}

/** The markdown marker for an embed — the inverse of `renderPdfEmbedHtml`. */
export function pdfEmbedMarkdown(src, width, height) {
  const w = toDimension(width, PDF_EMBED_DEFAULT_WIDTH)
  const h = toDimension(height, PDF_EMBED_DEFAULT_HEIGHT)
  return `[PDF](${src}){width=${w} height=${h}}`
}

/**
 * Drop a trailing PDF marker that a fixed-length preview truncation cut in half,
 * so a preview shows nothing rather than a dangling `[PDF](https://e.com/a.p…`.
 * Call it on the truncated string, before converting markdown to HTML.
 */
export function stripPartialPdfMarker(text) {
  if (!text || typeof text !== 'string' || !text.includes('[PDF]')) return text || ''
  return text.replace(PARTIAL_PDF_MARKDOWN_PATTERN, '')
}
